
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Gamepad, Zap, Trophy, Clock, Heart, Star } from 'lucide-react';

const GameFeatures = () => {
  const features = [
    {
      icon: <Gamepad className="w-10 h-10 text-yellow-300" />,
      title: "Simple Click Mechanics",
      description: "Pokemon Clicker features intuitive click-based gameplay that anyone can pick up and enjoy. Click to catch Pokemon, gather resources, and battle opponents with ease."
    },
    {
      icon: <Zap className="w-10 h-10 text-yellow-300" />,
      title: "Evolution System",
      description: "Watch your Pokemon grow and evolve as you accumulate experience points. Each evolution brings stronger stats and new abilities to help you progress further."
    },
    {
      icon: <Trophy className="w-10 h-10 text-yellow-300" />,
      title: "Gym Challenges",
      description: "Test your Pokemon team against gym leaders from various regions. Each victory earns you badges and unlocks new areas to explore and Pokemon to discover."
    },
    {
      icon: <Clock className="w-10 h-10 text-yellow-300" />,
      title: "Idle Progression",
      description: "Your journey continues even when you're not actively playing. Return to find new Pokemon caught, resources gathered, and progress made automatically."
    },
    {
      icon: <Heart className="w-10 h-10 text-yellow-300" />,
      title: "Hundreds of Pokemon",
      description: "Collect Pokemon from multiple generations, each with unique stats and abilities. From common creatures to legendary rarities, there are hundreds to discover."
    },
    {
      icon: <Star className="w-10 h-10 text-yellow-300" />,
      title: "Special Events",
      description: "Participate in limited-time events that offer rare Pokemon and exclusive rewards. Stay engaged with regularly updated content and seasonal challenges."
    }
  ];

  return (
    <section className="my-16" id="features">
      <h2 className="text-3xl font-bold mb-8 text-center text-yellow-300 drop-shadow-lg">Key Features</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {features.map((feature, index) => (
          <Card key={index} className="bg-blue-800/50 border-blue-600 shadow-lg hover:shadow-xl transition-all hover:transform hover:scale-105 duration-300">
            <CardContent className="p-6 flex flex-col items-center text-center">
              <div className="mb-4 p-3 bg-blue-700 rounded-full">
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold mb-2 text-yellow-200">{feature.title}</h3>
              <p className="text-blue-100">{feature.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-12">
        <Card className="bg-gradient-to-r from-red-900/70 to-yellow-900/70 border-yellow-600 shadow-lg">
          <CardContent className="p-6">
            <h3 className="text-2xl font-semibold mb-4 text-center text-yellow-200">The Pokemon Clicker Experience</h3>
            <p className="text-blue-100 leading-relaxed mb-4">
              Pokemon Clicker transforms the traditional Pokemon gaming experience into an accessible, always-progressing adventure that adapts to your play style. Whether you're a dedicated clicker who enjoys active play sessions or prefer to check in occasionally and let the idle mechanics do the work, the game accommodates both approaches seamlessly.
            </p>
            <p className="text-blue-100 leading-relaxed mb-4">
              The game's progression system is carefully designed to maintain engagement at every stage. Early gameplay focuses on rapid acquisition of common Pokemon and basic resources, providing instant gratification with each click. As you advance, the game gradually introduces more strategic elements, including team building, optimizing Pokemon abilities, and choosing the most efficient upgrade paths.
            </p>
            <p className="text-blue-100 leading-relaxed mb-4">
              One of the most satisfying aspects of Pokemon Clicker is watching your collection grow. The familiar joy of seeing a silhouette transform into a newly caught Pokemon never diminishes, and the game regularly introduces opportunities to add rare and powerful creatures to your team. The Pokedex tracking system provides additional motivation, challenging completionists to find every last species across the game's multiple regions.
            </p>
            <p className="text-blue-100 leading-relaxed">
              For Pokemon enthusiasts, the game offers a concentrated version of what makes the franchise special: the thrill of discovery, the satisfaction of training and evolution, and the pride of building a powerful team. For clicker game fans, it provides the perfect blend of immediate rewards and long-term goals that define the best titles in the genre. This combination makes Pokemon Clicker an engaging experience that keeps players returning day after day.
            </p>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default GameFeatures;
